import type { Dispatch, SetStateAction } from "react";
import { Table } from "components/table/table";
import { useFormatMoney } from "hooks/use-format-money";
import { type LandlordPropertiesData } from "modules/landlord-portal/models/overview";
import { IsOutgoingCell } from "modules/landlord-portal/screens/overview/components/is-outgoing-cell";
import { PropertyCell } from "modules/landlord-portal/screens/overview/components/property-cell";
import { StatusCell } from "modules/landlord-portal/screens/overview/components/status-cell";
import { RentScheduleEntryType } from "types/rent-schedule-entry";
import { formatAgreementDate } from "utils/date";

const rentScheduleEntryLabelMap = {
  [RentScheduleEntryType.Weeks]: "per week",
  [RentScheduleEntryType.Days]: "per day",
  [RentScheduleEntryType.Months]: "per month",
  [RentScheduleEntryType.Years]: "per year",
};

export function LandlordProperties({
  propertyOwnerships,
  isLoadingPropertyOwnerships,
  handleOnSetPage,
}: {
  propertyOwnerships?: LandlordPropertiesData;
  isLoadingPropertyOwnerships?: boolean;
  handleOnSetPage: Dispatch<SetStateAction<number>>;
}) {
  const formatMoney = useFormatMoney();

  return (
    <Table<LandlordPropertiesData["data"][number]>
      isLoading={isLoadingPropertyOwnerships}
      onRowClick={() => null}
      meta={propertyOwnerships?.pagination}
      setPage={(page) => handleOnSetPage(page)}
      isFetching={isLoadingPropertyOwnerships}
      // TODO: navigate to details of property
      // getRowHref={(row) => `/landlord/${row.id}`}
      columns={[
        {
          label: "Property",
          Cell: PropertyCell,
          whiteSpace: "nowrap",
        },
        {
          label: "Status",
          id: "status",
          Cell: StatusCell,
          whiteSpace: "nowrap",
          formatValue: (_v, row) => {
            return row.property_tenancy_name;
          },
        },
        {
          label: "Is outgoing?",
          id: "outgoing",
          Cell: IsOutgoingCell,
          whiteSpace: "nowrap",
          formatValue: (_v, row) => {
            return row.is_outgoing;
          },
        },
        {
          label: "Agreement",
          whiteSpace: "nowrap",
          formatValue: (_v, row) => {
            return `${formatAgreementDate(
              row.agreement_start_date,
            )} — ${formatAgreementDate(row.agreement_end_date)}`;
          },
        },
        {
          label: "Rent",
          id: "amount",
          whiteSpace: "nowrap",
          formatValue: (_v, row) => {
            if (!row.rent_amount || !row.frequency_unit) {
              return "–";
            }
            // convert rent_amount cents into proper fraction
            const amount = row.rent_amount / 100;
            return `${formatMoney(amount)} ${
              rentScheduleEntryLabelMap[row.frequency_unit.id]
            }`;
          },
        },
        {
          label: "Arrears amount",
          id: "arrears_amount",
          whiteSpace: "nowrap",
          formatValue: (_v, row) => {
            if (!row.rent_arrears_amount) {
              return "–";
            }
            // convert rent_amount cents into proper fraction
            const amount = row.rent_arrears_amount / 100;
            return `${formatMoney(amount)}`;
          },
        },
        {
          label: "Days in arrears",
          id: "days_in_arrears",
          whiteSpace: "nowrap",
          formatValue: (_v, row) => {
            if (!row.rent_arrears_days) {
              return "–";
            }
            return row.rent_arrears_days;
          },
        },
        {
          label: "Paid to",
          id: "paid_to_date",
          whiteSpace: "nowrap",
          formatValue: (_v, row) => formatAgreementDate(row.paid_to_date),
        },
      ]}
      rows={propertyOwnerships?.data ?? []}
      emptyProps={{
        emptyMessage: "No managed properties found.",
        emptyDesc: "Please check back later.",
      }}
    />
  );
}
